import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { FaExchangeAlt, FaCalendarAlt, FaFilter, FaCheckCircle, FaClock, FaTimesCircle, FaDownload, FaTimes } from 'react-icons/fa';

const TransactionsView = ({ userInfo, API_BASE_URL, auth }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('today');
  const [showDateRangePopup, setShowDateRangePopup] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen width for responsive behavior
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [dateFilter]);

  const fetchTransactions = async (filterType = dateFilter) => {
    try {
      setLoading(true);

      // Validate zone assignment
      if (!userInfo.zone_id) {
        console.warn('No zone assigned to field officer');
        setTransactions([]);
        setLoading(false);
        return;
      }

      let dateParam = '';
      const today = new Date().toISOString().split('T')[0];

      if (filterType === 'today') {
        dateParam = `&date=${today}`;
      } else if (filterType === 'week') {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        dateParam = `&date_from=${weekAgo.toISOString().split('T')[0]}&date_to=${today}`;
      } else if (filterType === 'month') {
        const monthAgo = new Date();
        monthAgo.setDate(monthAgo.getDate() - 30);
        dateParam = `&date_from=${monthAgo.toISOString().split('T')[0]}&date_to=${today}`;
      } else if (filterType === 'custom' && dateRange.from && dateRange.to) {
        dateParam = `&date_from=${dateRange.from}&date_to=${dateRange.to}`;
      }

      const response = await axios.get(
        `${API_BASE_URL}/transactions?userRole=Revenue Officer&user_id=${userInfo.user_id}&zone_id=${userInfo.zone_id}${dateParam}`,
        auth
      );
      setTransactions(response.data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'successful':
        return <FaCheckCircle color="#27ae60" />;
      case 'pending':
        return <FaClock color="#f39c12" />;
      case 'failed':
        return <FaTimesCircle color="#e74c3c" />;
      default:
        return <FaClock color="#95a5a6" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'successful':
        return { bg: '#d4edda', color: '#155724' };
      case 'pending':
        return { bg: '#fff3cd', color: '#856404' };
      case 'failed':
        return { bg: '#f8d7da', color: '#721c24' };
      default:
        return { bg: '#f8f9fa', color: '#6c757d' };
    }
  };

  // Date Range Filter Handler
  const handleDateRangeFilter = () => {
    if (!dateRange.from || !dateRange.to) {
      alert('Please select both From and To dates');
      return;
    }

    if (new Date(dateRange.from) > new Date(dateRange.to)) {
      alert('From date cannot be later than To date');
      return;
    }

    // Apply custom date range filter
    setDateFilter('custom');
    setShowDateRangePopup(false);

    // Fetch transactions with custom date range
    fetchTransactions('custom');
  };

  const resetDateRangeFilter = () => {
    setDateRange({ from: '', to: '' });
    setDateFilter('today');
    setShowDateRangePopup(false);
    fetchTransactions('today');
  };

  // Excel Download Function
  const downloadTransactionsExcel = () => {
    try {
      // Prepare data for Excel
      const excelData = transactions.map(transaction => ({
        'Transaction ID': transaction.transaction_id || '',
        'Date': transaction.transaction_time ? new Date(transaction.transaction_time).toLocaleDateString() : '',
        'Time': transaction.transaction_time ? new Date(transaction.transaction_time).toLocaleTimeString() : '',
        'Amount (GHS)': transaction.total_amount || 0,
        'Payment Method': transaction.payment_method || '',
        'Status': transaction.status || '',
        'Officer': transaction.officer_name || '',
        'MMDA': transaction.mmda_name || '',
        'Receipt Number': transaction.receipt_number || '',
        'Description': transaction.description || '',
        'Created Date': transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : ''
      }));

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const columnWidths = [
        { wch: 15 }, // Transaction ID
        { wch: 12 }, // Date
        { wch: 10 }, // Time
        { wch: 15 }, // Amount
        { wch: 15 }, // Payment Method
        { wch: 12 }, // Status
        { wch: 20 }, // Officer
        { wch: 20 }, // MMDA
        { wch: 15 }, // Receipt Number
        { wch: 30 }, // Description
        { wch: 15 }, // Created Date
      ];
      worksheet['!cols'] = columnWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

      // Generate filename with current date and filter
      const dateLabel = dateFilter === 'today' ? 'today' :
                       dateFilter === 'week' ? 'this_week' :
                       dateFilter === 'month' ? 'this_month' : 'all';
      const filename = `transactions_${dateLabel}_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download the file
      XLSX.writeFile(workbook, filename);

      alert(`✅ Transactions exported successfully! Downloaded as ${filename}`);
    } catch (error) {
      console.error('Error exporting transactions to Excel:', error);
      alert('❌ Error exporting transactions. Please try again.');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div>Loading transactions...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '0.5rem' : '1rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: isMobile ? 'flex-start' : 'space-between',
        alignItems: isMobile ? 'flex-start' : 'center',
        gap: isMobile ? '1rem' : '0',
        marginBottom: '2rem'
      }}>
        <h1 style={{ margin: 0, color: '#2c3e50', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
          <FaExchangeAlt style={{ marginRight: '0.5rem' }} />
          Transaction History
        </h1>

        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: '1rem', width: isMobile ? '100%' : 'auto' }}>
          <button
            onClick={downloadTransactionsExcel}
            style={{
              padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontSize: isMobile ? '0.8rem' : '0.85rem',
              fontWeight: '500',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <FaDownload />
            {isMobile ? 'Download' : 'Download Excel'}
          </button>
          <button
            onClick={() => setShowDateRangePopup(true)}
            style={{
              padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1rem',
              backgroundColor: '#9b59b6',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              fontSize: isMobile ? '0.8rem' : '0.85rem',
              fontWeight: '500',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <FaFilter />
            {isMobile ? 'Filter' : 'Filter by Date Range'}
          </button>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            style={{
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '0.25rem',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              width: isMobile ? '100%' : 'auto',
              minWidth: isMobile ? '100%' : '120px'
            }}
          >
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="all">All Time</option>
            {dateFilter === 'custom' && (
              <option value="custom">Custom Range ({dateRange.from} to {dateRange.to})</option>
            )}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#3498db' }}>
            {transactions.length}
          </h3>
          <p style={{ margin: 0, color: '#7f8c8d' }}>Total Transactions</p>
        </div>
        
        <div style={{
          backgroundColor: 'white',
          padding: '1rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#27ae60' }}>
            GHS {transactions
              .filter(t => t.status === 'successful')
              .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0)
              .toFixed(2)}
          </h3>
          <p style={{ margin: 0, color: '#7f8c8d' }}>Total Revenue</p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#27ae60' }}>
            {transactions.filter(t => t.status === 'successful').length}
          </h3>
          <p style={{ margin: 0, color: '#7f8c8d' }}>Successful</p>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1rem',
          borderRadius: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem', color: '#f39c12' }}>
            {transactions.filter(t => t.status === 'pending').length}
          </h3>
          <p style={{ margin: 0, color: '#7f8c8d' }}>Pending</p>
        </div>
      </div>

      {/* Transactions Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '0.5rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        {transactions.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: '#7f8c8d'
          }}>
            <FaExchangeAlt size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <h3>No transactions found</h3>
            <p>No transactions found for the selected period.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Date & Time
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Reference
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Payer
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Amount
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Status
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>
                    Payment Method
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => {
                  const statusStyle = getStatusColor(transaction.status);
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontSize: '0.9rem' }}>
                          {new Date(transaction.transaction_time).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                          {new Date(transaction.transaction_time).toLocaleTimeString()}
                        </div>
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.9rem', fontFamily: 'monospace' }}>
                        {transaction.reference_code || 'N/A'}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                          {transaction.payer_name || 'N/A'}
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                          {transaction.payer_phone || 'N/A'}
                        </div>
                      </td>
                      <td style={{ padding: '1rem', fontSize: '1rem', fontWeight: '600', color: '#27ae60' }}>
                        GHS {parseFloat(transaction.amount || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          backgroundColor: statusStyle.bg,
                          color: statusStyle.color
                        }}>
                          {getStatusIcon(transaction.status)}
                          {transaction.status || 'Unknown'}
                        </span>
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.9rem' }}>
                        {transaction.payment_method_name || 'N/A'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Date Range Filter Popup */}
      {showDateRangePopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: isMobile ? '1rem' : '2rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            width: isMobile ? '95vw' : '400px',
            maxWidth: '90vw'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '1.2rem' }}>
                <FaCalendarAlt style={{ marginRight: '0.5rem' }} />
                Filter by Date Range
              </h3>
              <button
                onClick={() => setShowDateRangePopup(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.2rem',
                  cursor: 'pointer',
                  color: '#7f8c8d',
                  padding: '0.25rem'
                }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Date Inputs */}
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#2c3e50'
                }}>
                  From Date:
                </label>
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '0.9rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: '500',
                  color: '#2c3e50'
                }}>
                  To Date:
                </label>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '0.25rem',
                    fontSize: '0.9rem'
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '1rem',
              justifyContent: isMobile ? 'stretch' : 'flex-end',
              alignItems: isMobile ? 'stretch' : 'center'
            }}>
              <button
                onClick={resetDateRangeFilter}
                style={{
                  padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.5rem',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  fontWeight: '500',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Reset
              </button>
              <button
                onClick={() => setShowDateRangePopup(false)}
                style={{
                  padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.5rem',
                  backgroundColor: '#e74c3c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  fontWeight: '500',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDateRangeFilter}
                style={{
                  padding: isMobile ? '0.6rem 1rem' : '0.75rem 1.5rem',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  fontWeight: '500',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionsView;
